<template>
    <div class="chat-area">
        <!-- Loading State -->
        <div v-if="isChatLoading" class="chat-loading">
            <div class="loading-content">
                <div class="loading-avatar">
                    <i class="fas fa-comments"></i>
                </div>
                <div class="loading-info">
                    <h3>Loading conversation...</h3>
                    <p>Please wait while we load the chat</p>
                </div>
                <div class="loading-spinner">
                    <div class="spinner"></div>
                </div>
            </div>
        </div>

        <!-- Active Chat -->
        <div v-else-if="currentChat" class="chat-active">
            <div class="chat-window">
                <!-- Chat Header -->
                <div class="conversation-header">
                    <div class="conversation-info">
                        <div class="conversation-avatar">
                            <div class="avatar-initials" :style="getAvatarStyle(currentChat && currentChat.jid)">
                                {{ getInitials(currentChat && (currentChat.name || currentChat.number)) }}
                            </div>
                            <div class="status-dot active"></div>
                        </div>
                        <div class="conversation-details">
                            <h3 class="conversation-name">{{ currentChat.name }}</h3>
                            <p class="conversation-number">{{ currentChat.number }}</p>
                        </div>
                    </div>
                    <div class="conversation-actions">
                        <div class="category-selector">
                            <select v-model="currentChat.category" @change="chatChangeCategory" class="category-select">
                                <option value="">All Categories</option>
                                <option v-for="(cat, idx) in category" :key="idx" :value="cat.name">
                                    {{ cat.name }}
                                </option>
                            </select>
                        </div>
                        <button v-if="isSendable" @click="closeCurrentSession" class="end-chat-btn">
                            <i class="fas fa-phone-slash"></i>
                            End Chat
                        </button>
                    </div>
                </div>

                <!-- Messages Area -->
                <div class="messages-container" ref="chatBox" @scroll="handleScroll">
                    <div class="messages-list">
                        <div 
                            v-for="(chat, idx) in chats" 
                            :key="idx" 
                            class="message-item"
                            :class="{ 'incoming': chat.from_me == 0, 'outgoing': chat.from_me == 1 }"
                        >
                            <!-- Incoming Message -->
                            <div v-if="chat.from_me == 0" class="message incoming-message" @mouseover="hoverChat(idx)" @mouseleave="unhoverChat()">
                                <div class="message-avatar">
                                    <div class="avatar-initials small" :style="getAvatarStyle(currentChat && currentChat.jid)">
                                        {{ getInitials(currentChat && (currentChat.name || currentChat.number)) }}
                                    </div>
                                </div>
                                <div class="message-content">
                                    <div class="message-bubble incoming">
                                        <p class="message-text">{{ chat.text }}</p>
                                        <div class="message-meta">
                                            <span class="message-time">{{ formatDateDetail(chat.created_at) }}</span>
                                            <div v-if="getHoverChat(idx)" class="message-actions">
                                                <button @click="insertOrder(idx)" class="action-button" title="Create Order">
                                                    <i class="fas fa-plus"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Outgoing Message -->
                            <div v-else class="message outgoing-message">
                                <div class="message-content">
                                    <div class="message-bubble outgoing">
                                        <p class="message-text">{{ chat.text }}</p>
                                        <div class="message-meta">
                                            <span class="message-time">{{ formatDateDetail(chat.created_at) }}</span>
                                            <div class="message-status">
                                                <i class="fas fa-check-double"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="message-menu">
                                    <button class="menu-button" title="Message options">
                                        <i class="fas fa-ellipsis-h"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Scroll to Bottom Button -->
                    <button
                        v-if="showScrollButton"
                        class="scroll-to-bottom"
                        @click="scrollToBottom"
                        title="Scroll to latest messages"
                    >
                        <i class="fas fa-chevron-down"></i>
                        <span v-if="unreadCount > 0" class="unread-indicator">{{ unreadCount }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="chat-empty">
            <div class="empty-content">
                <div class="empty-header">
                    <div class="empty-avatar">
                        <i class="fas fa-comments"></i>
                    </div>
                    <div class="empty-info">
                        <h2>Welcome to Customer Service</h2>
                        <p>Select a conversation to start chatting</p>
                    </div>
                </div>
                <div class="empty-message">
                    <div class="welcome-message">
                        <div class="message-avatar">
                            <div class="avatar-initials" :style="getAvatarStyle('welcome')">CS</div>
                        </div>
                        <div class="message-bubble">
                            <p>You can now send and receive messages without connecting your phone to the internet.</p>
                            <span class="message-time">Just now</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'MessageArea',
    props: {
        isChatLoading: {
            type: Boolean,
            default: false
        },
        currentChat: {
            type: Object,
            default: null
        },
        chats: {
            type: Array,
            default: () => []
        },
        isSendable: {
            type: Boolean,
            default: false
        },
        category: {
            type: Array,
            default: () => []
        },
        showScrollButton: {
            type: Boolean,
            default: false
        },
        unreadCount: {
            type: Number,
            default: 0
        },
        hover: {
            type: Object,
            default: () => ({})
        }
    },
    methods: {
        chatChangeCategory() {
            this.$emit('chat-change-category');
        },
        closeCurrentSession() {
            this.$emit('close-current-session');
        },
        insertOrder(idx) {
            this.$emit('insert-order', idx);
        },
        hoverChat(idx) {
            this.$emit('hover-chat', idx);
        },
        unhoverChat() {
            this.$emit('unhover-chat');
        },
        getHoverChat(idx) {
            return this.hover.idx === idx;
        },
        handleScroll() {
            this.$emit('scroll');
        },
        scrollToBottom() {
            this.$emit('scroll-to-bottom');
        },
        formatDateDetail(unix) {
            return moment(unix).format("LLL");
        },
        getInitials(name) {
            try {
                const cleaned = (name || '').toString().trim();
                if (!cleaned) return '?';
                if (/^\d[\d\s+()-]*$/.test(cleaned)) {
                    const digits = cleaned.replace(/\D/g, '');
                    return digits.slice(-2).toUpperCase();
                }
                const parts = cleaned.split(/\s+/).filter(Boolean);
                if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } catch (e) {
                return '?';
            }
        },
        getAvatarStyle(seed) {
            const colors = ['#F8BBD0', '#BBDEFB', '#C8E6C9', '#FFE0B2', '#D1C4E9', '#B2DFDB', '#FFCDD2', '#DCEDC8'];
            let hash = 0;
            const str = (seed || '').toString();
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash |= 0;
            }
            const index = Math.abs(hash) % colors.length;
            return {
                background: colors[index],
                color: '#1a1a1a'
            };
        }
    }
};
</script>

<style scoped>
/* Chat Area */
.chat-area {
    flex: 1;
    background: white;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
}

/* Chat Loading */
.chat-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
}

.loading-content {
    text-align: center;
    padding: 40px;
}

.loading-avatar {
    width: 80px;
    height: 80px;
    background: #e0e0e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 2rem;
    color: #757575;
}

.loading-info h3 {
    margin: 0 0 8px 0;
    color: #212121;
    font-size: 1.25rem;
    font-weight: 400;
    letter-spacing: 0.15px;
}

.loading-info p {
    margin: 0;
    color: #757575;
    font-size: 0.875rem;
    font-weight: 400;
    letter-spacing: 0.25px;
}

/* Active Chat */
.chat-active {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.chat-window {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Conversation Header */
.conversation-header {
    background: white;
    border-bottom: 1px solid #e0e0e0;
    padding: 16px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.conversation-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.conversation-avatar {
    position: relative;
}

.conversation-avatar img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
}

.conversation-avatar .avatar-initials {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.95rem;
    text-transform: uppercase;
}

.status-dot {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid white;
}

.status-dot.active {
    background: #28a745;
}

.conversation-details h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 500;
    color: #212121;
    letter-spacing: 0.15px;
}

.conversation-number {
    margin: 2px 0 0 0;
    font-size: 0.875rem;
    color: #757575;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.conversation-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.category-selector {
    position: relative;
}

.category-select {
    background: white;
    border: 1px solid #e0e0e0;
    color: #212121;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    min-width: 120px;
    font-family: 'Roboto', sans-serif;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.category-select:focus {
    border-color: #1976d2;
    outline: none;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.end-chat-btn {
    background: #f44336;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Roboto', sans-serif;
    letter-spacing: 0.25px;
    text-transform: uppercase;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
}

.end-chat-btn:hover {
    background: #d32f2f;
    box-shadow: 0 4px 8px rgba(244, 67, 54, 0.4);
}

.end-chat-btn:active {
    transform: scale(0.98);
    box-shadow: 0 1px 2px rgba(244, 67, 54, 0.3);
}

.action-menu {
    position: relative;
}

.menu-trigger {
    background: none;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #757575;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-trigger:hover {
    background: #f5f5f5;
    color: #212121;
}

/* Messages Container */
.messages-container {
    flex: 1;
    overflow-y: auto;
    background: #e5ddd5;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    padding: 16px;
}

.messages-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.message-item {
    display: flex;
    margin-bottom: 4px;
}

.message-item.incoming {
    justify-content: flex-start;
}

.message-item.outgoing {
    justify-content: flex-end;
}

.message {
    max-width: 70%;
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

.incoming-message {
    justify-content: flex-start;
}

.outgoing-message {
    justify-content: flex-end;
}

.message-avatar {
    flex-shrink: 0;
}

.message-avatar img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
}

.message-avatar .avatar-initials {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
}

.message-avatar .avatar-initials.small {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
}

.message-content {
    flex: 1;
}

.message-bubble {
    background: white;
    border-radius: 18px;
    padding: 12px 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    position: relative;
    max-width: 100%;
}

.message-bubble.incoming {
    background: white;
    color: #212529;
}

.message-bubble.outgoing {
    background: #e3f2fd;
    color: #212121;
}

.message-text {
    font-size: 0.875rem;
    line-height: 1.4;
    white-space: pre-line;
    margin: 0;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.message-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 6px;
    font-size: 0.7rem;
    color: #6c757d;
}

.message-time {
    font-size: 0.75rem;
    color: #757575;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.message-status {
    display: flex;
    align-items: center;
    gap: 2px;
}

.message-status i {
    font-size: 0.75rem;
    color: #4caf50;
}

.message-actions {
    margin-top: 6px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.incoming-message:hover .message-actions {
    opacity: 1;
}

.action-button {
    background: #4caf50;
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: 'Roboto', sans-serif;
    letter-spacing: 0.25px;
    text-transform: uppercase;
    box-shadow: 0 1px 2px rgba(76, 175, 80, 0.3);
}

.action-button:hover {
    background: #388e3c;
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.4);
}

.message-menu {
    margin-left: 8px;
}

.menu-button {
    background: none;
    border: none;
    color: #757575;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.menu-button:hover {
    background: rgba(0, 0, 0, 0.08);
}

/* Scroll to Bottom Button */
.scroll-to-bottom {
    position: absolute;
    bottom: 80px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #1976d2;
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 100;
    font-size: 1rem;
}

.scroll-to-bottom:hover {
    background: #1565c0;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 0 12px rgba(0, 0, 0, 0.15);
    transform: scale(1.05);
}

.unread-indicator {
    background: #f44336;
    color: white;
    border-radius: 50%;
    padding: 2px 6px;
    font-size: 0.75rem;
    font-weight: 500;
    min-width: 18px;
    text-align: center;
    position: absolute;
    top: -8px;
    right: -8px;
    line-height: 1.2;
}

/* Empty State */
.chat-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
}

.empty-content {
    text-align: center;
    padding: 40px;
    max-width: 400px;
}

.empty-header {
    margin-bottom: 32px;
}

.empty-avatar {
    width: 80px;
    height: 80px;
    background: #e0e0e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 2rem;
    color: #757575;
}

.empty-info h2 {
    margin: 0 0 8px 0;
    color: #212121;
    font-size: 1.5rem;
    font-weight: 400;
    letter-spacing: 0.15px;
}

.empty-info p {
    margin: 0;
    color: #757575;
    font-size: 1rem;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.empty-message {
    background: white;
    border-radius: 4px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
}

.welcome-message {
    display: flex;
    align-items: flex-end;
    gap: 12px;
}

.welcome-message .message-avatar img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
}

.welcome-message .message-bubble {
    background: #f5f5f5;
    border-radius: 18px;
    padding: 12px 16px;
    max-width: 300px;
}

.welcome-message .message-bubble p {
    margin: 0 0 4px 0;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #424242;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.welcome-message .message-time {
    font-size: 0.75rem;
    color: #757575;
    font-weight: 400;
    letter-spacing: 0.25px;
}

/* Loading Spinner */
.loading-spinner {
    margin-bottom: 16px;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #1976d2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
    .conversation-header {
        padding: 12px 16px;
    }

    .conversation-actions {
        gap: 8px;
    }

    .category-select {
        min-width: 100px;
        font-size: 0.8rem;
    }

    .end-chat-btn {
        padding: 6px 12px;
        font-size: 0.8rem;
    }

    .messages-container {
        padding: 12px;
    }

    .message-bubble {
        max-width: 85%;
    }

    .scroll-to-bottom {
        width: 44px;
        height: 44px;
        bottom: 70px;
        right: 16px;
    }

    .empty-content {
        padding: 20px;
    }

    .empty-avatar {
        width: 60px;
        height: 60px;
        font-size: 1.5rem;
    }

    .empty-info h2 {
        font-size: 1.3rem;
    }

    .empty-info p {
        font-size: 0.9rem;
    }
}
</style>
